import { apiClient } from '@shared/utils/apiClient.js'

/**
 * Gym composable
 * Provides methods for gym module API calls
 */
export function useGym() {
  // ==================== SETTINGS ====================

  /**
   * Get gym settings
   */
  const getSettings = async () => {
    return await apiClient.get('/v1/gym/settings')
  }

  /**
   * Update gym settings
   */
  const updateSettings = async (settingsData) => {
    return await apiClient.put('/v1/gym/settings', settingsData)
  }

  // ==================== PLANS ====================

  /**
   * List all plans
   */
  const listPlans = async () => {
    return await apiClient.get('/v1/gym/plans')
  }

  /**
   * Get plan by ID
   */
  const getPlan = async (id) => {
    return await apiClient.get(`/v1/gym/plans/${id}`)
  }

  /**
   * Create new plan
   */
  const createPlan = async (planData) => {
    return await apiClient.post('/v1/gym/plans', planData)
  }

  /**
   * Update plan
   */
  const updatePlan = async (id, planData) => {
    return await apiClient.put(`/v1/gym/plans/${id}`, planData)
  }

  /**
   * Delete (deactivate) plan
   */
  const deletePlan = async (id) => {
    return await apiClient.delete(`/v1/gym/plans/${id}`)
  }

  // ==================== MEMBERS ====================

  /**
   * List all members
   * @param {Object} options - Optional filter options
   * @param {string} options.search - Search query (name or phone)
   * @param {string} options.status - Filter by status (active, expiring, expired, inactive)
   * @param {number} options.limit - Limit on number of results
   * @param {number} options.offset - Offset for pagination
   */
  const listMembers = async (options = {}) => {
    const params = []
    if (options.search) params.push(`search=${encodeURIComponent(options.search)}`)
    if (options.status) params.push(`status=${options.status}`)
    if (options.limit > 0) params.push(`limit=${options.limit}`)
    if (options.offset > 0) params.push(`offset=${options.offset}`)
    const url = params.length ? `/v1/gym/members?${params.join('&')}` : '/v1/gym/members'
    return await apiClient.get(url)
  }

  /**
   * Get member by ID
   */
  const getMember = async (id) => {
    return await apiClient.get(`/v1/gym/members/${id}`)
  }

  /**
   * Get member detail with payment history
   */
  const getMemberDetail = async (id) => {
    return await apiClient.get(`/v1/gym/members/${id}/detail`)
  }

  /**
   * Create new member
   * @param {Object} memberData - Member data
   * @param {string} memberData.name - Member name (required)
   * @param {string} memberData.phone - 10-digit phone (required)
   * @param {string} memberData.email - Email (optional)
   * @param {number} memberData.plan_id - Plan ID for enrollment (optional)
   * @param {number} memberData.amount - Payment amount (optional, requires plan_id)
   * @param {string} memberData.payment_mode - Payment mode (optional, requires plan_id)
   */
  const createMember = async (memberData) => {
    return await apiClient.post('/v1/gym/members', memberData)
  }

  /**
   * Update member
   */
  const updateMember = async (id, memberData) => {
    return await apiClient.put(`/v1/gym/members/${id}`, memberData)
  }

  /**
   * Upload member photo
   * @param {Blob} photoBlob - The photo blob from webcam capture
   * @returns {Promise<{photo_url: string}>}
   */
  const uploadMemberPhoto = async (photoBlob) => {
    const formData = new FormData()
    formData.append('photo', photoBlob, 'member-photo.jpg')
    return await apiClient.upload('/v1/gym/members/photo', formData)
  }

  // ==================== PAYMENTS ====================

  /**
   * List payments
   * @param {Object} options - Optional filter options
   * @param {number} options.member_id - Filter by member ID
   * @param {string} options.from_date - Filter from date (YYYY-MM-DD)
   * @param {string} options.to_date - Filter to date (YYYY-MM-DD)
   * @param {boolean} options.include_void - Include voided payments
   * @param {number} options.limit - Limit on number of results
   * @param {number} options.offset - Offset for pagination
   */
  const listPayments = async (options = {}) => {
    const params = []
    if (options.member_id) params.push(`member_id=${options.member_id}`)
    if (options.from_date) params.push(`from_date=${options.from_date}`)
    if (options.to_date) params.push(`to_date=${options.to_date}`)
    if (options.search) params.push(`search=${encodeURIComponent(options.search)}`)
    if (options.include_void) params.push(`include_void=true`)
    if (options.limit > 0) params.push(`limit=${options.limit}`)
    if (options.offset > 0) params.push(`offset=${options.offset}`)
    const url = params.length ? `/v1/gym/payments?${params.join('&')}` : '/v1/gym/payments'
    return await apiClient.get(url)
  }

  /**
   * Get payment by ID
   */
  const getPayment = async (id) => {
    return await apiClient.get(`/v1/gym/payments/${id}`)
  }

  /**
   * Create payment (renew membership)
   * @param {Object} paymentData - Payment data
   * @param {number} paymentData.member_id - Member ID (required)
   * @param {number} paymentData.plan_id - Plan ID (required)
   * @param {number} paymentData.amount - Payment amount (required)
   * @param {string} paymentData.payment_mode - Payment mode (required)
   * @param {string} paymentData.notes - Optional notes
   */
  const createPayment = async (paymentData) => {
    return await apiClient.post('/v1/gym/payments', paymentData)
  }

  /**
   * Void a payment
   * @param {number} id - Payment ID
   * @param {string} reason - Reason for voiding (required)
   */
  const voidPayment = async (id, reason) => {
    return await apiClient.post(`/v1/gym/payments/${id}/void`, { reason })
  }

  /**
   * Resend payment receipt via WhatsApp
   */
  const resendReceipt = async (id) => {
    return await apiClient.post(`/v1/gym/payments/${id}/resend-receipt`)
  }

  // ==================== DASHBOARD ====================

  /**
   * Get dashboard stats
   */
  const getDashboard = async () => {
    return await apiClient.get('/v1/gym/dashboard')
  }

  // ==================== GLOBAL SEARCH ====================

  /**
   * Global search across members and plans
   * @param {string} query - Search query (minimum 2 characters)
   * @returns {Promise<{query: string, members: Array, plans: Array}>}
   */
  const globalSearch = async (query) => {
    return await apiClient.get(`/v1/gym/search?q=${encodeURIComponent(query)}`)
  }

  // ==================== BUSINESS ====================

  /**
   * Get business settings
   */
  const getBusiness = async () => {
    return await apiClient.get('/v1/business')
  }

  /**
   * Update business settings
   */
  const updateBusiness = async (businessData) => {
    return await apiClient.put('/v1/business', businessData)
  }

  // ==================== MESSAGING SETTINGS ====================

  /**
   * Get messaging settings (WhatsApp, SMS config)
   */
  const getMessagingSettings = async () => {
    return await apiClient.get('/v1/messaging/settings')
  }

  /**
   * Update messaging settings
   * @param {Object} data - Settings to update
   * @param {boolean} data.whatsapp_enabled - Enable/disable WhatsApp messages
   */
  const updateMessagingSettings = async (data) => {
    return await apiClient.put('/v1/messaging/settings', data)
  }

  // ==================== MESSAGE LOGS ====================

  /**
   * Get message logs with optional filters
   * @param {Object} options - Filter options
   * @param {string} options.status - Filter by status (pending, sent, delivered, read, failed)
   * @param {string} options.from_date - Filter from date (YYYY-MM-DD)
   * @param {string} options.to_date - Filter to date (YYYY-MM-DD)
   * @param {string} options.phone - Filter by recipient phone (partial match)
   * @param {string} options.entity_type - Filter by related entity type (e.g., gym_payment)
   * @param {number} options.entity_id - Filter by related entity ID (e.g., member_id)
   * @param {string} options.sort_by - Sort column (created_at, sent_at, status, recipient_phone, template_name)
   * @param {string} options.sort_order - Sort order (asc, desc)
   * @param {number} options.limit - Limit on number of results (max 100)
   * @param {number} options.offset - Offset for pagination
   * @returns {Promise<{data: Array, pagination: Object}>}
   */
  const getMessageLogs = async (options = {}) => {
    const params = []
    if (options.status) params.push(`status=${options.status}`)
    if (options.from_date) params.push(`from_date=${options.from_date}`)
    if (options.to_date) params.push(`to_date=${options.to_date}`)
    if (options.phone) params.push(`phone=${encodeURIComponent(options.phone)}`)
    if (options.entity_type) params.push(`entity_type=${options.entity_type}`)
    if (options.entity_id) params.push(`entity_id=${options.entity_id}`)
    if (options.sort_by) params.push(`sort_by=${options.sort_by}`)
    if (options.sort_order) params.push(`sort_order=${options.sort_order}`)
    if (options.limit > 0) params.push(`limit=${options.limit}`)
    if (options.offset > 0) params.push(`offset=${options.offset}`)
    // Use gym-specific endpoint that enriches logs with member names
    const url = params.length ? `/v1/gym/messages?${params.join('&')}` : '/v1/gym/messages'
    return await apiClient.get(url)
  }

  // Return all methods
  return {
    // Settings
    getSettings,
    updateSettings,

    // Plans
    listPlans,
    getPlan,
    createPlan,
    updatePlan,
    deletePlan,

    // Members
    listMembers,
    getMember,
    getMemberDetail,
    createMember,
    updateMember,
    uploadMemberPhoto,

    // Payments
    listPayments,
    getPayment,
    createPayment,
    voidPayment,
    resendReceipt,

    // Dashboard
    getDashboard,

    // Global Search
    globalSearch,

    // Business
    getBusiness,
    updateBusiness,

    // Messaging Settings
    getMessagingSettings,
    updateMessagingSettings,

    // Message Logs
    getMessageLogs,
  }
}
